import React, { useEffect, useState, useRef } from 'react';
import { useFormikContext, ErrorMessage } from 'formik';
import { Text } from '@rsos/components/capstone/base';
import {
  formatStartsWithOneNumber,
  formatPhoneNumber,
} from '@rsos/components/capstone/base/form/inputs/MultiEntryInput/helpers';
import { StyledInput } from './AviveSearchForm.styles';

export const formatPhone = (value, config = {}) => {
  const { onlyDashes = false } = config;
  const valueStartsWithOne = value.startsWith('1');
  const formattedNumber = valueStartsWithOne
    ? formatStartsWithOneNumber(value)
    : formatPhoneNumber(value);
  if (onlyDashes) {
    // first remove all whitespace and then parentheses and replace with dashes.
    // why? so that the user can copy and paste the number and be compatible
    // with the universal search, etc.
    return `${formattedNumber}`.replace(/\s/g, '').replace(/[()]/g, '-');
  }
  return formattedNumber;
};

const PhoneField = ({
  name,
  placeholder = '',
  initialValue = '',
  disabled = false,
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const {
    errors,
    touched,
    values,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext();
  const userHasInteracted = useRef(false);

  useEffect(() => {
    // Format the value from formiks internal state at mount.
    const value = values[name];
    if (value) {
      const formattedNumber = formatPhone(value);
      setDisplayValue(formattedNumber);
      return () => {
        setFieldTouched(name, false);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // When an initialValue is given, format it & update formik state
    // but only if the user hasn't interacted with the field yet.
    if (initialValue && !userHasInteracted.current) {
      setFieldValue(name, initialValue, true);
      const formattedNumber = formatPhone(initialValue);
      setDisplayValue(formattedNumber);
    }
  }, [initialValue, name, setFieldValue]);

  const handleChange = event => {
    userHasInteracted.current = true;
    const { value } = event.target;
    const unformattedValue = value.replace(/\D/g, '');
    const formattedNumber = formatPhone(value);

    setDisplayValue(formattedNumber);
    setFieldValue(name, unformattedValue, true);
  };

  return (
    <>
      <StyledInput
        name={name}
        data-testid="phone-field"
        disabled={disabled}
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        onBlur={() => setFieldTouched(name, true)}
        error={errors[name] && touched[name]}
      />
      <Text size="small" pt={2}>
        <ErrorMessage name={name} />
      </Text>
    </>
  );
};

export default PhoneField;
